import { Link as RouterLink } from 'react-router-dom';
import {
  resolveLatestChipBackground,
  resolveLatestChipForeground,
} from '@/features/shared/components/tableSystem/TableLatestCellStyles';
import {
  CLONE_META_DETAIL_GLYPH_SX,
  TableStatusMetaContent,
} from '@/features/shared/components/tableSystem/TableMetaCells';
import {
  resolveProjectDisplayStatus,
  resolveProjectDisplayStatusLabel,
} from '@/features/shared/utils/projectStatusPresentation';
import { formatDuration, getStatusTextColor, Link, resolveStatusChipTone, Text } from '@/ui';
import { getStatusIconComponent } from '@/ui/composites/table';
import { formatDate } from '@/ui/formatters/formatDate';
import { formatRelativeTime } from '@/ui/formatters/formatRelativeTime';
import { Box } from '@/ui/primitives';
import { EventOutlinedIcon, TimerOutlinedIcon } from '@/ui/primitives/icons';
import { TableCellContainer } from './TableCells';
import type { TestListItemDto, TestRunStatus } from '@flowqa/shared';
import type { ReactElement } from 'react';

type TestsLatestStatusCellProps = {
  disableLinks: boolean;
  latestAsMeta: boolean;
  onProjectSelect: (projectId: string) => void;
  onStatusSelect?: (status: TestRunStatus | null) => void;
  row: TestListItemDto;
  selectedProjectId?: string | null;
};

export const TestsLatestStatusCell = ({
  disableLinks,
  latestAsMeta,
  onProjectSelect,
  onStatusSelect,
  row,
  selectedProjectId = null,
}: TestsLatestStatusCellProps): ReactElement => {
  const latestRun = row.latestRun ?? null;
  const rawStatus = latestRun?.status ?? null;
  const status = resolveProjectDisplayStatus(rawStatus, Boolean(selectedProjectId));

  if (latestAsMeta) {
    const statusLabel =
      resolveProjectDisplayStatusLabel(rawStatus, Boolean(selectedProjectId)) || '-';
    const statusTone = resolveStatusChipTone(status);
    const durationLabel =
      latestRun?.durationMs !== null && latestRun?.durationMs !== undefined
        ? formatDuration(latestRun.durationMs)
        : '';
    const timestamp = latestRun?.timestamp ?? null;
    const timestampLabel = timestamp ? formatRelativeTime(timestamp) : '';
    const timestampTitle = timestamp ? formatDate(timestamp, { includeTime: true }) : '';
    const resolvedTimestampTitle =
      timestampTitle || (timestamp instanceof Date ? timestamp.toISOString() : timestamp);
    const runNumber = latestRun?.runNumber ?? null;
    const executionHref =
      runNumber !== null && row.slug && !disableLinks
        ? `/tests/${row.slug}/runs/${runNumber}`
        : null;

    return (
      <TableCellContainer gap="none">
        <TableStatusMetaContent
          statusLabel={statusLabel}
          statusHref={executionHref}
          onStatusClick={(event) => {
            event.stopPropagation();
            onProjectSelect(row.projectId);
          }}
          chipTone={statusTone}
          chipIcon={<Box component={getStatusIconComponent(status)} />}
          chipBackgroundColor={(theme) => resolveLatestChipBackground(theme, statusTone)}
          chipForegroundColor={(theme) => resolveLatestChipForeground(theme, statusTone)}
          durationLabel={durationLabel}
          durationIcon={<TimerOutlinedIcon sx={CLONE_META_DETAIL_GLYPH_SX} />}
          timestampLabel={timestampLabel}
          timestampTitle={resolvedTimestampTitle}
          timestampIcon={<EventOutlinedIcon sx={CLONE_META_DETAIL_GLYPH_SX} />}
        />
      </TableCellContainer>
    );
  }

  const isClickable = Boolean(status && onStatusSelect);
  const label = rawStatus
    ? resolveProjectDisplayStatusLabel(rawStatus, Boolean(selectedProjectId))
    : 'Untested';
  const runNumber = latestRun?.runNumber ?? null;
  const executionHref =
    runNumber !== null && row.slug ? `/tests/${row.slug}/runs/${runNumber}` : null;
  const labelNode =
    executionHref && !disableLinks ? (
      <Link
        component={RouterLink}
        to={executionHref}
        underline="hover"
        color="inherit"
        onClick={(event) => {
          event.stopPropagation();
          onProjectSelect(row.projectId);
        }}
      >
        <Text role="body" component="span" color="inherit">
          {label}
        </Text>
      </Link>
    ) : (
      label
    );

  if (isClickable && !executionHref) {
    return (
      <Box
        component="span"
        sx={{ cursor: 'pointer' }}
        onClick={() => {
          onStatusSelect?.(status ?? null);
        }}
      >
        <Text
          role="body"
          component="span"
          sx={{
            color: (theme) => getStatusTextColor(theme, status),
          }}
          transform="uppercase"
        >
          {labelNode}
        </Text>
      </Box>
    );
  }

  return (
    <Text
      role="body"
      component="span"
      sx={{
        color: (theme) => getStatusTextColor(theme, status),
      }}
      transform="uppercase"
    >
      {labelNode}
    </Text>
  );
};
